import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Ban,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  CircleSlash,
  Phone,
  Plus,
  Share2,
  SlidersHorizontal,
  Trash2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import { useConfig } from "@/lib/useConfig";
import { usePaged, ShowMore } from "@/lib/usePaged";
import { Kpi, PageHead } from "@/components/Bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const WINDOW = 14;

// Stable per-studio colour so visit blocks are colour-coded by studio on the calendar + list.
const studioHue = (id) => {
  let h = 0;
  for (const c of String(id || "")) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
};
const visitCellStyle = (id) => ({
  backgroundColor: `hsl(${studioHue(id)} 78% 90%)`,
  color: `hsl(${studioHue(id)} 55% 26%)`,
  borderColor: `hsl(${studioHue(id)} 60% 72%)`,
});
const studioDotStyle = (id) => ({ backgroundColor: `hsl(${studioHue(id)} 70% 48%)` });

const shift = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const dayLabel = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

// Open a click-to-send WhatsApp message to the ASSIGNED team's captain. The full message (customer,
// car model, service, scheduled date/time, studio location) is assembled server-side from the job,
// so it's always complete and accurate. No WhatsApp API is used — just a wa.me deep link.
const openCaptainNotify = async (jobId) => {
  if (!jobId) return;
  const w = window.open("", "_blank");
  try {
    const { data } = await api.get(`/jobs/${jobId}/captain-notify`);
    if (data.whatsapp_link) {
      if (w) w.location.href = data.whatsapp_link;
    } else {
      if (w) w.close();
      toast.warning(`No captain mobile set for ${data.captain_name || "this team"} — add it in Teams management`);
    }
  } catch (e) {
    if (w) w.close();
    toast.error(apiError(e.response?.data?.detail) || "Could not build the captain message");
  }
};

// Same, for a team RESERVATION (studio + date/time + team). Sends the reservation detail to the
// captain via a wa.me link — no WhatsApp API.
const openVisitCaptainNotify = async (visitId) => {
  if (!visitId) return;
  const w = window.open("", "_blank");
  try {
    const { data } = await api.get(`/scheduling/visits/${visitId}/captain-notify`);
    if (data.whatsapp_link) {
      if (w) w.location.href = data.whatsapp_link;
    } else {
      if (w) w.close();
      toast.warning(`No captain mobile set for ${data.captain_name || "this team"} — add it in Teams management`);
    }
  } catch (e) {
    if (w) w.close();
    toast.error(apiError(e.response?.data?.detail) || "Could not build the reservation message");
  }
};

export default function Scheduling() {
  const { user } = useAuth();
  const canWrite = can(user, "scheduling:write");
  const isAdmin = user.is_network || user.role === "network_admin";
  const canBlockDetailer = user.role === "sales_manager_products" || user.role === "network_admin";
  const skillOptions = useConfig("services");

  const [teams, setTeams] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [windowStart, setWindowStart] = useState(null);
  const [studios, setStudios] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [menu, setMenu] = useState(null);
  const [detail, setDetail] = useState(null);
  const [booking, setBooking] = useState(null);
  const [bookingForm, setBookingForm] = useState({ studio_id: "", team_id: "", date: "", time: "", note: "" });
  const [confirmed, setConfirmed] = useState(null);
  const [blockFor, setBlockFor] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [teamForm, setTeamForm] = useState(null);
  const longPress = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [mobileView, setMobileView] = useState("grid");
  const [fTeam, setFTeam] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [visits, setVisits] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [visitForm, setVisitForm] = useState(null);
  const [visitDetail, setVisitDetail] = useState(null);
  const [detailerOptions, setDetailerOptions] = useState([]);
  const [blockForm, setBlockForm] = useState(null);
  const [extBlockDetail, setExtBlockDetail] = useState(null);

  const loadTeams = useCallback(() => {
    api.get("/teams").then(({ data }) => setTeams(data));
  }, []);

  const loadCalendar = useCallback((ws) => {
    api
      .get("/scheduling/calendar", {
        params: { days_count: WINDOW, ...(ws ? { week_start: ws } : {}) },
      })
      .then(({ data }) => {
        setCalendar(data);
        setWindowStart(data.week_start);
      });
  }, []);

  const loadJobs = useCallback(() => {
    api.get("/bookings").then(({ data }) => setBookings(data));
  }, []);

  const loadVisits = useCallback(() => {
    api.get("/scheduling/visits").then(({ data }) => setVisits(data)).catch(() => {});
    api.get("/jobs").then(({ data }) => setJobs(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadTeams();
    loadCalendar(null);
    loadJobs();
    loadVisits();
    if (isAdmin) api.get("/studios").then(({ data }) => setStudios(data));
    if (canBlockDetailer) api.get("/b2b/detailers").then(({ data }) => setDetailerOptions(data)).catch(() => {});
  }, [loadTeams, loadCalendar, loadJobs, loadVisits, isAdmin, canBlockDetailer]);

  useEffect(() => {
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  const teamRows = useMemo(
    () =>
      (calendar?.detailers || []).map((row) => ({
        ...row,
        team: teams.find((t) => t.id === row.detailer_id),
      })),
    [calendar, teams]
  );
  const paged = usePaged(teamRows);
  const bookingPaged = usePaged(bookings);

  const cellState = (day) => {
    if (!day.available) return "blocked";
    if (day.jobs.length > 0) return "booked";
    if (day.visit) return "reserved";
    return "free";
  };

  const distanceMode = calendar?.travel_buffer_mode === "distance";


  const openBooking = (row, day) => {
    setMenu(null);
    setBooking({ row, date: day.date });
    setBookingForm({
      studio_id: row.studio_id || (isAdmin ? "" : user.studio_id) || "",
      team_id: row.detailer_id || "",
      date: day.date,
      time: "",
      note: "",
    });
    setConfirmed(null);
  };

  const confirmBooking = async () => {
    try {
      const teamId = bookingForm.team_id || booking.row.detailer_id;
      const studioId = bookingForm.studio_id || booking.row.studio_id;
      if (!studioId) return toast.error("Select a studio");
      if (!teamId) return toast.error("Select an installer team");
      if (!bookingForm.date) return toast.error("Pick a date");
      const { data } = await api.post(`/scheduling/visits`, {
        studio_id: studioId,
        team_id: teamId,
        start_date: bookingForm.date,
        reserve_time: bookingForm.time || "",
        note: bookingForm.note || "",
      });
      setConfirmed({
        visit_id: data.id,
        date: bookingForm.date,
        time: bookingForm.time,
        team: data.team_name || booking.row.name,
        studio: data.studio_name || booking.row.studio_name,
      });
      toast.success(`${data.team_name || booking.row.name} reserved for ${bookingForm.date}`);
      loadCalendar(windowStart);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const setBlocked = async (row, date, blocked, reason) => {
    try {
      await api.post(`/detailers/${row.detailer_id}/availability`, {
        date,
        available: !blocked,
        reason: reason || "",
      });
      toast.success(`${row.name} · ${date} marked ${blocked ? "blocked" : "free"}`);
      setBlockFor(null);
      setBlockReason("");
      setMenu(null);
      loadCalendar(windowStart);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const clearBooking = async (day) => {
    try {
      await Promise.all(day.jobs.map((j) => api.delete(`/jobs/${j.job_id}/schedule`)));
      toast.success("Booking cleared");
      setMenu(null);
      setDetail(null);
      loadCalendar(windowStart);
      loadJobs();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveTeam = async () => {
    try {
      const payload = {
        name: teamForm.name,
        leader_name: teamForm.leader_name,
        leader_mobile: teamForm.leader_mobile,
        skills: teamForm.skills,
        home_base: teamForm.home_base,
        active: teamForm.active,
        installer_type: teamForm.installer_type || (isAdmin ? "hq" : "studio"),
        studio_id: (teamForm.installer_type || "studio") === "hq"
          ? null
          : teamForm.studio_id || null,
      };
      if (teamForm.id) await api.put(`/teams/${teamForm.id}`, payload);
      else await api.post("/teams", payload);
      toast.success("Installer team saved");
      setTeamForm(null);
      loadTeams();
      loadCalendar(windowStart);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveVisit = async () => {
    try {
      await api.post("/scheduling/visits", {
        studio_id: isAdmin ? visitForm.studio_id : user.studio_id,
        team_id: visitForm.team_id,
        start_date: visitForm.start_date,
        end_date: visitForm.end_date || visitForm.start_date,
        note: visitForm.note || "",
      });
      toast.success("Visit reserved");
      setVisitForm(null);
      loadVisits();
      loadCalendar(windowStart);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const linkVisitJob = async (visit, jobId) => {
    try {
      await api.post(`/scheduling/visits/${visit.id}/link-job`, { job_id: jobId || null });
      toast.success(jobId ? "Job linked to this visit" : "Job unlinked");
      loadVisits();
      loadCalendar(windowStart);
      setVisitDetail(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const cancelVisit = async (visit) => {
    try {
      await api.delete(`/scheduling/visits/${visit.id}`);
      toast.success("Visit cancelled");
      loadVisits();
      loadCalendar(windowStart);
      setVisitDetail(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveDetailerBlock = async () => {
    try {
      const isOther = blockForm.detailer_type === "other";
      const opt = detailerOptions.find((o) => o.id === blockForm.detailer_id);
      await api.post("/scheduling/detailer-blocks", {
        detailer_type: blockForm.detailer_type,
        detailer_id: isOther ? null : blockForm.detailer_id,
        detailer_name: isOther ? blockForm.detailer_name.trim() : opt?.name || "",
        start_date: blockForm.start_date,
        end_date: blockForm.end_date || blockForm.start_date,
        note: blockForm.note || "",
      });
      toast.success("Detailer dates blocked");
      setBlockForm(null);
      loadCalendar(windowStart);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeDetailerBlock = async (blockId) => {
    try {
      await api.delete(`/scheduling/detailer-blocks/${blockId}`);
      toast.success("Detailer block removed");
      setExtBlockDetail(null);
      loadCalendar(windowStart);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };


  const today = new Date().toISOString().slice(0, 10);
  const upcomingVisits = visits.filter((v) => v.end_date >= today);

  return (
    <div>
      <PageHead
        title="Installer Scheduling"
        subtitle={`${
          isAdmin || !user.studio_name
            ? "HQ installers across the network"
            : `${user.studio_name} · your installers and HQ crews on your jobs`
        } · teams do not log in; slots are shared over WhatsApp`}
      >
        {canWrite && (
          <Button
            data-testid="add-team-btn"
            className="min-h-[44px]"
            onClick={() =>
              setTeamForm({
                name: "",
                leader_name: "",
                leader_mobile: "",
                skills: [],
                home_base: "",
                active: true,
                installer_type: isAdmin ? "hq" : "studio",
                studio_id: isAdmin ? "" : user.studio_id,
              })
            }
          >
            <Plus className="mr-2 h-4 w-4" /> Add installer
          </Button>
        )}
        {canWrite && (
          <Button
            variant="outline"
            data-testid="reserve-visit-btn"
            className="min-h-[44px]"
            onClick={() =>
              setVisitForm({
                studio_id: isAdmin ? "" : user.studio_id,
                team_id: "",
                start_date: "",
                end_date: "",
                note: "",
              })
            }
          >
            <CalendarCheck className="mr-2 h-4 w-4" /> Reserve visit
          </Button>
        )}
        {canBlockDetailer && (
          <Button
            variant="outline"
            data-testid="block-detailer-btn"
            className="min-h-[44px]"
            onClick={() =>
              setBlockForm({
                detailer_type: "accredited",
                detailer_id: "",
                detailer_name: "",
                start_date: "",
                end_date: "",
                note: "",
              })
            }
          >
            <Ban className="mr-2 h-4 w-4" /> Block detailer
          </Button>
        )}
      </PageHead>

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <Kpi label="Installer teams" value={teams.length} testid="kpi-detailers" />
        <Kpi
          label="Active teams"
          value={teams.filter((t) => t.active).length}
          testid="kpi-available"
          accent="text-emerald-600"
        />
        <Kpi
          label="Pending requests"
          value={bookings.filter((b) => b.status === "pending").length}
          testid="kpi-pending-bookings"
        />
      </div>

      {/* MOBILE toolbar: view toggle + week nav (always visible under lg) */}
      <div className="mb-4 space-y-3 lg:hidden" data-testid="mobile-schedule-toolbar">
        <div className="flex items-center justify-between gap-2">
          <div
            className="inline-flex rounded-md border border-brand-line bg-white p-0.5"
            data-testid="mobile-view-toggle"
          >
            <button
              data-testid="mobile-view-grid"
              onClick={() => setMobileView("grid")}
              className={`min-h-[40px] rounded px-4 text-sm font-semibold transition-colors ${
                mobileView === "grid" ? "bg-brand text-white" : "text-slate-600"
              }`}
            >
              Grid
            </button>
            <button
              data-testid="mobile-view-list"
              onClick={() => setMobileView("list")}
              className={`min-h-[40px] rounded px-4 text-sm font-semibold transition-colors ${
                mobileView === "list" ? "bg-brand text-white" : "text-slate-600"
              }`}
            >
              List
            </button>
          </div>
          {mobileView === "list" && (
            <Button
              size="sm"
              variant="outline"
              className="min-h-[44px]"
              data-testid="mobile-filter-toggle"
              onClick={() => setShowFilters((s) => !s)}
            >
              <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters
            </Button>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <Button
            size="sm"
            variant="outline"
            className="min-h-[44px]"
            data-testid="mobile-prev-week"
            onClick={() => loadCalendar(shift(windowStart, -WINDOW))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs font-medium text-slate-700">
            {calendar
              ? `${dayLabel(calendar.days[0])} — ${dayLabel(calendar.days[calendar.days.length - 1])}`
              : ""}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="min-h-[44px]"
            data-testid="mobile-next-week"
            onClick={() => loadCalendar(shift(windowStart, WINDOW))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        {mobileView === "list" && showFilters && (
          <div className="space-y-3 rounded-md border border-brand-line bg-slate-50 p-3" data-testid="mobile-filters">
            <div>
              <Label className="text-xs">Team member</Label>
              <Select value={fTeam} onValueChange={setFTeam}>
                <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="mobile-filter-team">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All teams</SelectItem>
                  {teamRows.map((r) => (
                    <SelectItem key={r.detailer_id} value={r.detailer_id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  className="mt-1.5 min-h-[44px]"
                  data-testid="mobile-filter-from"
                  value={fFrom}
                  onChange={(e) => setFFrom(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  className="mt-1.5 min-h-[44px]"
                  data-testid="mobile-filter-to"
                  value={fTo}
                  onChange={(e) => setFTo(e.target.value)}
                />
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="min-h-[44px] text-slate-500"
              data-testid="mobile-filter-clear"
              onClick={() => { setFTeam("all"); setFFrom(""); setFTo(""); }}
            >
              Clear filters
            </Button>
          </div>
        )}
      </div>

      {/* MOBILE vertical list view */}
      <div className={`lg:hidden ${mobileView === "list" ? "block" : "hidden"}`} data-testid="mobile-schedule">
        <Card className="border-brand-line shadow-none">
          <CardContent className="space-y-4 pt-6">
            {(() => {
              const narrowed = fTeam !== "all" || !!fFrom || !!fTo;
              const rows = fTeam === "all" ? teamRows : teamRows.filter((r) => r.detailer_id === fTeam);
              if (rows.length === 0)
                return <p className="py-8 text-center text-sm text-slate-500">No teams to show.</p>;
              return rows.map((row) => {
                const days = row.days.filter(
                  (d) => (!fFrom || d.date >= fFrom) && (!fTo || d.date <= fTo)
                );
                const entries = days.filter((day) => {
                  const job = day.jobs[0];
                  if (job && job.day_index > 0) return false; // skip continuation days
                  const st = cellState(day);
                  if (row.external) return st === "blocked"; // external detailers: only show blocks
                  return st !== "free" || narrowed; // free days only when narrowed
                });
                return (
                  <div
                    key={row.detailer_id}
                    className="overflow-hidden rounded-md border border-brand-line"
                    data-testid={`mobile-team-${row.detailer_id}`}
                  >
                    <div className="flex items-start justify-between gap-2 border-b border-slate-100 bg-brand-tint/30 p-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-brand-ink">{row.name}</p>
                        {row.external ? (
                          <p className="truncate text-xs font-medium text-rose-700">{row.studio_name}</p>
                        ) : (
                          <>
                            <p className="truncate text-xs text-slate-500">
                              {row.team?.leader_name || "—"} · {row.team?.leader_mobile || "—"}
                            </p>
                            {isAdmin && (
                              <p className="truncate text-xs text-crimson-600">
                                {row.installer_type === "hq" ? "HQ installer" : row.studio_name}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                      {canWrite && !row.external && (isAdmin || row.installer_type !== "hq") && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="min-h-[44px] shrink-0"
                          data-testid={`mobile-edit-team-${row.detailer_id}`}
                          onClick={() =>
                            setTeamForm({
                              id: row.detailer_id,
                              name: row.name,
                              leader_name: row.team?.leader_name || "",
                              leader_mobile: row.team?.leader_mobile || "",
                              skills: row.skills || [],
                              home_base: row.home_base || "",
                              active: row.team?.active ?? true,
                              studio_id: row.studio_id,
                            })
                          }
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                    <div className="divide-y divide-slate-100">
                      {entries.length === 0 && (
                        <p className="px-3 py-4 text-center text-xs text-slate-400">
                          No bookings in this range. Pick a team or dates in Filters to book free days.
                        </p>
                      )}
                      {entries.map((day) => {
                        const st = cellState(day);
                        const job = day.jobs[0];
                        return (
                          <div key={day.date} className="p-3" data-testid={`mobile-entry-${row.detailer_id}-${day.date}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-semibold text-brand-ink">{dayLabel(day.date)}</span>
                              <Badge
                                data-testid={`mobile-entry-status-${row.detailer_id}-${day.date}`}
                                className={`border-0 capitalize ${
                                  st === "free"
                                    ? "bg-emerald-50 text-emerald-700"
                                    : st === "booked"
                                      ? "bg-amber-100 text-amber-900"
                                      : "bg-rose-50 text-rose-700"
                                }`}
                              >
                                {st}
                              </Badge>
                            </div>
                            {st === "booked" && (
                              <div className="mt-2 space-y-2">
                                {day.jobs.map((j) => (
                                  <div key={j.job_id} className="rounded-md bg-slate-50 p-2.5">
                                    <Link
                                      to={`/jobs/${j.job_id}`}
                                      className="block text-sm font-medium text-brand-ink hover:text-crimson-600"
                                    >
                                      {j.customer_name}
                                    </Link>
                                    <p className="text-xs text-slate-500">
                                      {(j.services || []).join(", ") || "—"} · {j.studio_name || row.studio_name}
                                      {j.span_days > 1 ? ` · ${j.span_days}-day job` : ""}
                                      {j.travel_buffer_hours != null ? ` · ${j.travel_buffer_hours}h travel` : ""}
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={() => openCaptainNotify(j.job_id)}
                                        data-testid={`mobile-wa-${j.job_id}`}
                                        className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700"
                                      >
                                        <Share2 className="h-3.5 w-3.5" /> Message captain
                                      </button>
                                      {canWrite && (
                                        <Button
                                          variant="outline"
                                          className="min-h-[44px] flex-1 border-rose-200 text-rose-600 hover:bg-rose-50"
                                          data-testid={`mobile-clear-${j.job_id}`}
                                          onClick={async () => {
                                            await api.delete(`/jobs/${j.job_id}/schedule`);
                                            toast.success("Booking cleared");
                                            loadCalendar(windowStart);
                                            loadJobs();
                                          }}
                                        >
                                          <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Clear
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {st === "blocked" && (
                              <div className="mt-2 flex items-center justify-between gap-2">
                                <span className="text-xs text-slate-500">{day.reason || "Blocked"}</span>
                                {row.external
                                  ? canBlockDetailer && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="min-h-[44px] border-rose-200 text-rose-600 hover:bg-rose-50"
                                        data-testid={`mobile-remove-block-${row.detailer_id}-${day.date}`}
                                        onClick={() => removeDetailerBlock(day.block_id)}
                                      >
                                        Remove block
                                      </Button>
                                    )
                                  : canWrite && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="min-h-[44px]"
                                        data-testid={`mobile-unblock-${row.detailer_id}-${day.date}`}
                                        onClick={() => setBlocked(row, day.date, false)}
                                      >
                                        Unblock
                                      </Button>
                                    )}
                              </div>
                            )}
                            {st === "free" && !row.external && canWrite && (
                              <div className="mt-2 flex gap-2">
                                <Button
                                  className="min-h-[44px] flex-1"
                                  data-testid={`mobile-book-${row.detailer_id}-${day.date}`}
                                  onClick={() => openBooking(row, day)}
                                >
                                  <CalendarCheck className="mr-1.5 h-4 w-4" /> Book
                                </Button>
                                <Button
                                  variant="outline"
                                  className="min-h-[44px]"
                                  data-testid={`mobile-block-${row.detailer_id}-${day.date}`}
                                  onClick={() => { setBlockFor({ row, day }); setBlockReason(""); }}
                                >
                                  <Ban className="mr-1.5 h-4 w-4" /> Block
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </CardContent>
        </Card>
      </div>

      <Card
        className={`border-brand-line shadow-none lg:block ${mobileView === "grid" ? "block" : "hidden"}`}
        data-testid="grid-card"
      >
        <CardHeader className="hidden flex-row flex-wrap items-center justify-between gap-3 space-y-0 lg:flex">
          <div>
            <CardTitle className="text-base">Team availability grid</CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              One job per team per day · multi-day jobs span their full duration ·{" "}
              {calendar
                ? distanceMode
                  ? "travel time is entered per booking"
                  : `${calendar.travel_buffer_hours}h fixed travel buffer`
                : ""}
              . Click a green cell to book · right-click or long-press any cell for more actions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="min-h-[44px]"
              data-testid="calendar-prev-week"
              onClick={() => loadCalendar(shift(windowStart, -WINDOW))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span data-testid="calendar-week-label" className="text-xs font-medium text-slate-700">
              {calendar
                ? `${dayLabel(calendar.days[0])} — ${dayLabel(calendar.days[calendar.days.length - 1])}`
                : ""}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="min-h-[44px]"
              data-testid="calendar-next-week"
              onClick={() => loadCalendar(shift(windowStart, WINDOW))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <p className="flex items-center gap-1.5 px-3 pt-3 text-xs text-slate-400 lg:hidden" data-testid="grid-swipe-hint">
            <ChevronLeft className="h-3 w-3" /> Swipe sideways to see more days
            <ChevronRight className="h-3 w-3" />
          </p>
          <div
            className="overflow-x-auto overscroll-x-contain"
            style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-x pan-y" }}
            data-testid="grid-scroll"
          >
            <table className="w-full border-collapse text-sm" data-testid="capacity-calendar">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 min-w-[132px] border-b border-brand-line bg-white p-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 sm:min-w-[200px] sm:p-3 sm:text-xs">
                    Installer team
                  </th>
                  {(calendar?.days || []).map((d) => (
                    <th
                      key={d}
                      className="min-w-[92px] border-b border-brand-line p-2 text-center text-[11px] font-semibold text-slate-600 sm:min-w-[112px]"
                    >
                      {dayLabel(d)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.visible.map((row) => (
                  <tr key={row.detailer_id} data-testid={`team-row-${row.detailer_id}`}>
                    <td className="sticky left-0 z-10 border-b border-slate-100 bg-white p-3 align-top">
                      <p className="text-sm font-semibold text-brand-ink">{row.name}</p>
                      {row.external ? (
                        <p className="mt-0.5 inline-flex items-center gap-1 rounded bg-rose-50 px-1.5 py-0.5 text-[11px] font-medium text-rose-700">
                          <Ban className="h-3 w-3" /> {row.studio_name}
                        </p>
                      ) : (
                        <>
                          <p className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Users className="h-3 w-3" /> {row.team?.leader_name || "—"}
                          </p>
                          <p className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Phone className="h-3 w-3" /> {row.team?.leader_mobile || "—"}
                          </p>
                          {isAdmin && (
                            <p className="text-xs text-crimson-600">
                              {row.installer_type === "hq" ? "HQ installer" : row.studio_name}
                            </p>
                          )}
                        </>
                      )}
                      {canWrite && !row.external && (isAdmin || row.installer_type !== "hq") && (
                        <button
                          data-testid={`edit-team-${row.detailer_id}`}
                          onClick={() =>
                            setTeamForm({
                              id: row.detailer_id,
                              name: row.name,
                              leader_name: row.team?.leader_name || "",
                              leader_mobile: row.team?.leader_mobile || "",
                              skills: row.skills || [],
                              home_base: row.home_base || "",
                              active: row.team?.active ?? true,
                              studio_id: row.studio_id,
                            })
                          }
                          className="mt-1 text-xs font-medium text-slate-500 underline-offset-2 hover:underline"
                        >
                          Edit team
                        </button>
                      )}
                    </td>
                    {row.days.map((day, i) => {
                      const job = day.jobs[0];
                      const continuation = job && job.day_index > 0 && i - job.day_index >= 0;
                      if (continuation) return null;
                      const span = job
                        ? Math.min(job.span_days - job.day_index, row.days.length - i)
                        : 1;
                      const state = cellState(day);
                      const isExt = row.external;
                      const tint = {
                        free: "bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
                        booked: "bg-amber-100 text-amber-900 hover:bg-amber-200",
                        blocked: "bg-rose-50 text-rose-700 hover:bg-rose-100",
                        reserved: "border",
                      }[state];
                      const cellTint =
                        isExt && state === "free"
                          ? "bg-white text-slate-300 cursor-default"
                          : tint;
                      return (
                        <td
                          key={day.date}
                          colSpan={span}
                          className="border-b border-l border-slate-100 p-1 align-top"
                        >
                          <button
                            data-testid={`cell-${row.detailer_id}-${day.date}`}
                          onClick={() => {
                            if (row.external) {
                              if (state === "blocked" && canBlockDetailer)
                                setExtBlockDetail({ row, day });
                            } else if (state === "blocked")
                                setDetail({ row, day, kind: "blocked" });
                              else if (day.jobs.length > 0)
                                setDetail({ row, day, kind: "booked" });
                              else if (state === "reserved")
                                setVisitDetail({ ...day.visit, date: day.date, team_name: row.name });
                              else if (canWrite) openBooking(row, day);
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (canWrite && !row.external) setMenu({ row, day });
                          }}
                          onTouchStart={() => {
                            longPress.current = setTimeout(() => {
                              if (canWrite && !row.external) setMenu({ row, day });
                            }, 500);
                          }}
                            onTouchEnd={() => clearTimeout(longPress.current)}
                            onTouchMove={() => clearTimeout(longPress.current)}
                            style={state === "reserved" ? visitCellStyle(day.visit.studio_id) : undefined}
                            className={`min-h-[44px] w-full rounded p-1.5 text-left text-[11px] font-medium transition-colors duration-200 ${cellTint}`}
                          >
                            {state === "blocked" && (
                              <span data-testid={`cell-status-${row.detailer_id}-${day.date}`}>
                                {isExt ? `Blocked${day.reason ? ` · ${day.reason}` : ""}` : "Blocked"}
                              </span>
                            )}
                            {state === "free" && (
                              <span data-testid={`cell-status-${row.detailer_id}-${day.date}`}>
                                {isExt ? "—" : "Free"}
                              </span>
                            )}
                            {state === "reserved" && (
                              <span data-testid={`cell-status-${row.detailer_id}-${day.date}`}>
                                {day.visit.job_id ? "Visit" : "Reserved"} · {day.visit.studio_name}
                              </span>
                            )}
                            {state === "booked" && (
                              <span data-testid={`cell-status-${row.detailer_id}-${day.date}`}>
                                {job.customer_name} · {job.studio_name || row.studio_name}
                                {job.span_days > 1 ? ` · ${job.span_days}-day job` : ""}
                              </span>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ShowMore paged={paged} testid="teams-show-more" />
        </CardContent>
      </Card>

      {menu && createPortal(
        <div
          data-testid="cell-context-menu"
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/40 p-4"
          onClick={() => setMenu(null)}
        >
          <div
            className="w-full max-w-xs rounded-md border border-brand-line bg-white p-2 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {menu.row.name} · {dayLabel(menu.day.date)}
            </p>
            <button
              data-testid="menu-book-slot"
              onClick={() => openBooking(menu.row, menu.day)}
              disabled={!menu.day.available || menu.day.free_slots === 0}
              className="flex min-h-[44px] w-full items-center gap-2 rounded px-3 text-sm text-slate-700 hover:bg-brand-tint/60 disabled:opacity-40"
            >
              <CalendarCheck className="h-4 w-4" /> Book a slot
            </button>
            {menu.day.available ? (
              <button
                data-testid="menu-block-date"
                onClick={() => {
                  setBlockFor({ row: menu.row, day: menu.day });
                  setBlockReason("");
                  setMenu(null);
                }}
                className="flex min-h-[44px] w-full items-center gap-2 rounded px-3 text-sm text-slate-700 hover:bg-brand-tint/60"
              >
                <Ban className="h-4 w-4" /> Block this date
              </button>
            ) : (
              <button
                data-testid="menu-unblock-date"
                onClick={() => setBlocked(menu.row, menu.day.date, false)}
                className="flex min-h-[44px] w-full items-center gap-2 rounded px-3 text-sm text-slate-700 hover:bg-brand-tint/60"
              >
                <CircleCheck className="h-4 w-4" /> Unblock this date
              </button>
            )}
            <button
              data-testid="menu-clear-booking"
              onClick={() => clearBooking(menu.day)}
              disabled={menu.day.jobs.length === 0}
              className="flex min-h-[44px] w-full items-center gap-2 rounded px-3 text-sm text-rose-600 hover:bg-rose-50 disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" /> Clear booking
            </button>
          </div>
        </div>,
        document.body
      )}

      <Card className="mt-6 border-brand-line shadow-none" data-testid="upcoming-visits-card">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Upcoming studio visits</CardTitle>
            <p className="mt-1 text-xs text-slate-500">
              Teams reserved for studios — colour-coded per studio. Link the planned job to each visit.
            </p>
          </div>
          {canWrite && (
            <Button
              size="sm"
              variant="outline"
              className="min-h-[40px]"
              data-testid="reserve-visit-btn-2"
              onClick={() =>
                setVisitForm({
                  studio_id: isAdmin ? "" : user.studio_id,
                  team_id: "",
                  start_date: "",
                  end_date: "",
                  note: "",
                })
              }
            >
              <CalendarCheck className="mr-1.5 h-4 w-4" /> Reserve
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {upcomingVisits.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500" data-testid="no-visits">
              No upcoming visits reserved.
            </p>
          )}
          <div className="divide-y divide-slate-100" data-testid="visits-list">
            {upcomingVisits.map((v) => (
              <div
                key={v.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                data-testid={`visit-row-${v.id}`}
              >
                <div className="flex min-w-0 items-start gap-2.5">
                  <span
                    className="mt-1 h-3 w-3 shrink-0 rounded-full"
                    style={studioDotStyle(v.studio_id)}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brand-ink">{v.studio_name}</p>
                    <p className="text-xs text-slate-500">
                      {v.team_name} ·{" "}
                      {v.start_date === v.end_date
                        ? dayLabel(v.start_date)
                        : `${dayLabel(v.start_date)} – ${dayLabel(v.end_date)}`}
                      {v.note ? ` · ${v.note}` : ""}
                    </p>
                    {v.job ? (
                      <Link
                        to={`/jobs/${v.job.id}`}
                        data-testid={`visit-job-${v.id}`}
                        className="text-xs font-medium text-crimson-600 hover:underline"
                      >
                        Job: {v.job.customer_name} · {(v.job.services || []).join(", ") || v.job.status}
                      </Link>
                    ) : (
                      <span className="text-xs text-slate-400">No job linked yet</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    data-testid={`visit-status-${v.id}`}
                    className={`border-0 capitalize ${
                      v.status === "linked"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {v.status}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="min-h-[40px]"
                    data-testid={`visit-manage-${v.id}`}
                    onClick={() => setVisitDetail(v)}
                  >
                    Manage
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6 border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-base">Booking requests</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {bookings.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500">No booking requests yet.</p>
          )}
          <div className="divide-y divide-slate-100" data-testid="bookings-table">
            {bookingPaged.visible.map((b) => (
              <div
                key={b.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              >
                <div>
                  <p className="text-sm font-semibold text-brand-ink">{b.customer_name}</p>
                  <p className="text-xs text-slate-500">
                    {b.service_type} · {b.date}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge
                    data-testid={`booking-status-${b.id}`}
                    className={`border-0 capitalize ${
                      b.status === "confirmed"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {b.status}
                  </Badge>
                  {b.status === "confirmed" ? (
                    <span className="flex items-center gap-1.5 text-xs text-slate-600">
                      <CalendarCheck className="h-3.5 w-3.5" /> {b.detailer_name}
                    </span>
                  ) : (
                    canWrite && (
                      <Select
                        onValueChange={async (v) => {
                          try {
                            await api.post(`/bookings/${b.id}/confirm`, { detailer_id: v });
                            toast.success("Booking confirmed");
                            loadJobs();
                            loadCalendar(windowStart);
                          } catch (e) {
                            toast.error(apiError(e.response?.data?.detail));
                          }
                        }}
                      >
                        <SelectTrigger
                          className="min-h-[44px] w-[220px]"
                          data-testid={`confirm-booking-${b.id}`}
                        >
                          <SelectValue placeholder="Assign a team" />
                        </SelectTrigger>
                        <SelectContent>
                          {teams
                            .filter((t) => t.active)
                            .map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
          <ShowMore paged={bookingPaged} testid="bookings-show-more" />
        </CardContent>
      </Card>

      {/* cell detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-md" data-testid="cell-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {detail?.row.name} · {detail ? dayLabel(detail.day.date) : ""}
            </DialogTitle>
          </DialogHeader>
          {detail?.kind === "blocked" ? (
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-sm font-medium text-rose-700">
                <Ban className="h-4 w-4" /> Blocked
              </p>
              <p className="text-sm text-slate-600" data-testid="blocked-reason">
                {detail.day.reason || "No reason recorded."}
              </p>
              {canWrite && (
                <Button
                  variant="outline"
                  className="min-h-[44px]"
                  data-testid="detail-unblock-btn"
                  onClick={() => {
                    setBlocked(detail.row, detail.day.date, false);
                    setDetail(null);
                  }}
                >
                  Unblock date
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {(detail?.day.jobs || []).map((j) => (
                <div key={j.job_id} className="rounded-md border border-brand-line p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">
                    Booked at {j.studio_name || detail.row.studio_name}
                  </p>
                  <Link
                    to={`/jobs/${j.job_id}`}
                    data-testid={`calendar-job-${j.job_id}`}
                    className="mt-1 block text-sm font-semibold text-brand-ink hover:text-crimson-600"
                  >
                    {j.customer_name}
                  </Link>
                  <p className="text-xs text-slate-500">
                    {(j.services || []).join(", ") || "—"} · {j.span_days} day(s) from{" "}
                    {j.start_date} · {j.status}
                    {j.travel_buffer_hours != null ? ` · ${j.travel_buffer_hours}h travel` : ""}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => openCaptainNotify(j.job_id)}
                      data-testid="booking-whatsapp-share"
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-emerald-600 px-4 text-xs font-semibold text-white transition-colors duration-200 hover:bg-emerald-700"
                    >
                      <Share2 className="h-3.5 w-3.5" /> Message captain
                    </button>
                    {canWrite && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-h-[44px]"
                        data-testid={`detail-clear-${j.job_id}`}
                        onClick={async () => {
                          await api.delete(`/jobs/${j.job_id}/schedule`);
                          toast.success("Booking cleared");
                          setDetail(null);
                          loadCalendar(windowStart);
                          loadJobs();
                        }}
                      >
                        Clear booking
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {canWrite && detail?.day.free_slots > 0 && (
                <Button
                  variant="outline"
                  className="min-h-[44px]"
                  data-testid="detail-book-more-btn"
                  onClick={() => {
                    const d = detail;
                    setDetail(null);
                    openBooking(d.row, d.day);
                  }}
                >
                  Book this day
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* block date */}
      <Dialog open={!!blockFor} onOpenChange={(o) => !o && setBlockFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Block {blockFor?.row.name} · {blockFor ? dayLabel(blockFor.day.date) : ""}
            </DialogTitle>
          </DialogHeader>
          <div>
            <Label>Reason (optional)</Label>
            <Input
              className="mt-1.5 min-h-[44px]"
              data-testid="block-reason-input"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="Travel to Pune / leave"
            />
          </div>
          <DialogFooter>
            <Button
              className="min-h-[44px]"
              data-testid="confirm-block-btn"
              onClick={() => setBlocked(blockFor.row, blockFor.day.date, true, blockReason)}
            >
              Block date
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* booking form */}
      <Dialog open={!!booking} onOpenChange={(o) => !o && setBooking(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Reserve {booking?.row.name} · {booking ? dayLabel(booking.date) : ""}
            </DialogTitle>
          </DialogHeader>
          {confirmed ? (
            <div className="space-y-4">
              <p className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                <CircleCheck className="h-4 w-4" /> {confirmed.team} reserved at {confirmed.studio} for{" "}
                {confirmed.date}{confirmed.time ? ` · ${confirmed.time}` : ""}
              </p>
              <button
                type="button"
                onClick={() => openVisitCaptainNotify(confirmed.visit_id)}
                data-testid="booking-whatsapp-share"
                className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-emerald-600 px-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-emerald-700"
              >
                <Share2 className="h-4 w-4" /> Message captain
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {isAdmin && (
                <div>
                  <Label>Studio</Label>
                  <Select
                    value={bookingForm.studio_id}
                    onValueChange={(v) => setBookingForm((f) => ({ ...f, studio_id: v }))}
                  >
                    <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="booking-studio-select">
                      <SelectValue placeholder="Select studio" />
                    </SelectTrigger>
                    <SelectContent>
                      {studios.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Installer team</Label>
                <Select
                  value={bookingForm.team_id}
                  onValueChange={(v) => setBookingForm((f) => ({ ...f, team_id: v }))}
                >
                  <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="booking-team-select">
                    <SelectValue placeholder="Select installer team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    className="mt-1.5 min-h-[44px]"
                    data-testid="booking-date-input"
                    value={bookingForm.date}
                    onChange={(e) => setBookingForm((f) => ({ ...f, date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input
                    type="time"
                    className="mt-1.5 min-h-[44px]"
                    data-testid="booking-time-input"
                    value={bookingForm.time}
                    onChange={(e) => setBookingForm((f) => ({ ...f, time: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  data-testid="booking-note-input"
                  value={bookingForm.note}
                  onChange={(e) => setBookingForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Any detail for the team"
                />
              </div>
            </div>
          )}
          {!confirmed && (
            <DialogFooter>
              <Button
                data-testid="confirm-assign-btn"
                className="min-h-[44px]"
                onClick={confirmBooking}
                disabled={!(bookingForm.studio_id || booking?.row.studio_id) || !bookingForm.team_id || !bookingForm.date}
              >
                Reserve slot
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* reserve visit */}
      <Dialog open={!!visitForm} onOpenChange={(o) => !o && setVisitForm(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reserve-visit-dialog">
          <DialogHeader>
            <DialogTitle>Reserve a studio visit</DialogTitle>
            <DialogDescription>
              Block a team for a studio on specific dates. Other studios can't book the same team then.
            </DialogDescription>
          </DialogHeader>
          {visitForm && (
            <div className="space-y-4">
              {isAdmin && (
                <div>
                  <Label>Studio</Label>
                  <Select
                    value={visitForm.studio_id}
                    onValueChange={(v) => setVisitForm((f) => ({ ...f, studio_id: v }))}
                  >
                    <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="visit-studio-select">
                      <SelectValue placeholder="Select studio" />
                    </SelectTrigger>
                    <SelectContent>
                      {studios.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Installer team</Label>
                <Select
                  value={visitForm.team_id}
                  onValueChange={(v) => setVisitForm((f) => ({ ...f, team_id: v }))}
                >
                  <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="visit-team-select">
                    <SelectValue placeholder="Select a team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.filter((t) => t.active).map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}{t.installer_type === "hq" ? " (HQ)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>From</Label>
                  <Input
                    type="date"
                    className="mt-1.5 min-h-[44px]"
                    data-testid="visit-from"
                    value={visitForm.start_date}
                    onChange={(e) => setVisitForm((f) => ({ ...f, start_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>To (optional)</Label>
                  <Input
                    type="date"
                    className="mt-1.5 min-h-[44px]"
                    data-testid="visit-to"
                    value={visitForm.end_date}
                    onChange={(e) => setVisitForm((f) => ({ ...f, end_date: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  data-testid="visit-note"
                  value={visitForm.note}
                  onChange={(e) => setVisitForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="What's planned for this visit"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              className="min-h-[44px]"
              data-testid="save-visit-btn"
              onClick={saveVisit}
              disabled={
                !visitForm?.team_id ||
                !visitForm?.start_date ||
                (isAdmin && !visitForm?.studio_id)
              }
            >
              Reserve visit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* visit detail / manage */}
      <Dialog open={!!visitDetail} onOpenChange={(o) => !o && setVisitDetail(null)}>
        <DialogContent className="sm:max-w-md" data-testid="visit-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {visitDetail?.studio_name} ·{" "}
              {visitDetail
                ? visitDetail.start_date === visitDetail.end_date
                  ? dayLabel(visitDetail.start_date)
                  : `${dayLabel(visitDetail.start_date)} – ${dayLabel(visitDetail.end_date)}`
                : ""}
            </DialogTitle>
            <DialogDescription>Link the planned job to this visit, or cancel the reservation.</DialogDescription>
          </DialogHeader>
          {visitDetail && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Team: <span className="font-medium text-brand-ink">{visitDetail.team_name}</span>
                {visitDetail.note ? ` · ${visitDetail.note}` : ""}
              </p>
              <div>
                <Label>Linked job</Label>
                <Select
                  value={visitDetail.job_id || ""}
                  onValueChange={(v) => linkVisitJob(visitDetail, v)}
                >
                  <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="visit-link-job-select">
                    <SelectValue placeholder="Pick a job for this studio" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs
                      .filter((j) => j.studio_id === visitDetail.studio_id)
                      .map((j) => (
                        <SelectItem key={j.id} value={j.id}>
                          {j.customer_name} · {(j.services || []).join(", ") || j.status}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {jobs.filter((j) => j.studio_id === visitDetail.studio_id).length === 0 && (
                  <p className="mt-1.5 text-xs text-slate-500" data-testid="visit-no-jobs-hint">
                    No open jobs for this studio yet.
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="min-h-[44px] bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid="visit-detail-whatsapp-btn"
                  onClick={() => openVisitCaptainNotify(visitDetail.id)}
                >
                  <Share2 className="mr-1.5 h-3.5 w-3.5" /> Send via WhatsApp
                </Button>
                {visitDetail.job_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[44px]"
                    data-testid="visit-unlink-btn"
                    onClick={() => linkVisitJob(visitDetail, null)}
                  >
                    Unlink job
                  </Button>
                )}
                {canWrite && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="min-h-[44px] border-rose-200 text-rose-600 hover:bg-rose-50"
                    data-testid="visit-cancel-btn"
                    onClick={() => cancelVisit(visitDetail)}
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Cancel visit
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* block detailer dates (Sales Manager / Admin) */}
      <Dialog open={!!blockForm} onOpenChange={(o) => !o && setBlockForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="block-detailer-dialog">
          <DialogHeader>
            <DialogTitle>Block detailer dates</DialogTitle>
            <DialogDescription>
              Reserve a detailer's availability for a date range. This blocks those dates so they
              can't be double-booked — it does not assign a job.
            </DialogDescription>
          </DialogHeader>
          {blockForm && (
            <div className="space-y-4">
              <div>
                <Label>Installer type</Label>
                <Select
                  value={blockForm.detailer_type}
                  onValueChange={(v) =>
                    setBlockForm((f) => ({ ...f, detailer_type: v, detailer_id: "", detailer_name: "" }))
                  }
                >
                  <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="block-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accredited">Accredited detailer</SelectItem>
                    <SelectItem value="non_accredited">Non-accredited detailer</SelectItem>
                    <SelectItem value="other">Other (type a name)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {blockForm.detailer_type === "other" ? (
                <div>
                  <Label>Detailer name *</Label>
                  <Input
                    className="mt-1.5 min-h-[44px]"
                    data-testid="block-other-name-input"
                    value={blockForm.detailer_name}
                    onChange={(e) => setBlockForm((f) => ({ ...f, detailer_name: e.target.value }))}
                    placeholder="Enter installer / detailer name"
                  />
                </div>
              ) : (
                <div>
                  <Label>
                    {blockForm.detailer_type === "accredited" ? "Accredited detailer" : "Non-accredited detailer"} *
                  </Label>
                  <Select
                    value={blockForm.detailer_id}
                    onValueChange={(v) => setBlockForm((f) => ({ ...f, detailer_id: v }))}
                  >
                    <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="block-detailer-select">
                      <SelectValue placeholder="Select a detailer" />
                    </SelectTrigger>
                    <SelectContent>
                      {detailerOptions
                        .filter((o) =>
                          blockForm.detailer_type === "accredited"
                            ? o.type === "accredited"
                            : o.type === "prospect"
                        )
                        .map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {detailerOptions.filter((o) =>
                    blockForm.detailer_type === "accredited"
                      ? o.type === "accredited"
                      : o.type === "prospect"
                  ).length === 0 && (
                    <p className="mt-1.5 text-xs text-slate-500" data-testid="block-no-detailers-hint">
                      No {blockForm.detailer_type === "accredited" ? "accredited" : "non-accredited"} detailers found.
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>From *</Label>
                  <Input
                    type="date"
                    className="mt-1.5 min-h-[44px]"
                    data-testid="block-from"
                    value={blockForm.start_date}
                    onChange={(e) => setBlockForm((f) => ({ ...f, start_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>To (optional)</Label>
                  <Input
                    type="date"
                    className="mt-1.5 min-h-[44px]"
                    data-testid="block-to"
                    value={blockForm.end_date}
                    onChange={(e) => setBlockForm((f) => ({ ...f, end_date: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  data-testid="block-note"
                  value={blockForm.note}
                  onChange={(e) => setBlockForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Reason / what's reserved"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              className="min-h-[44px]"
              data-testid="save-block-btn"
              onClick={saveDetailerBlock}
              disabled={
                !blockForm?.start_date ||
                (blockForm?.detailer_type === "other"
                  ? !blockForm?.detailer_name?.trim()
                  : !blockForm?.detailer_id)
              }
            >
              Block dates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* external detailer block detail / remove */}
      <Dialog open={!!extBlockDetail} onOpenChange={(o) => !o && setExtBlockDetail(null)}>
        <DialogContent className="sm:max-w-md" data-testid="ext-block-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {extBlockDetail?.row.name} · {extBlockDetail ? dayLabel(extBlockDetail.day.date) : ""}
            </DialogTitle>
            <DialogDescription>{extBlockDetail?.row.studio_name}</DialogDescription>
          </DialogHeader>
          {extBlockDetail && (
            <div className="space-y-4">
              <p className="flex items-center gap-2 text-sm font-medium text-rose-700">
                <Ban className="h-4 w-4" /> Blocked
              </p>
              <p className="text-sm text-slate-600" data-testid="ext-block-reason">
                {extBlockDetail.day.reason || "No note recorded."}
              </p>
              {canBlockDetailer && (
                <Button
                  variant="outline"
                  className="min-h-[44px] border-rose-200 text-rose-600 hover:bg-rose-50"
                  data-testid="ext-block-remove-btn"
                  onClick={() => removeDetailerBlock(extBlockDetail.day.block_id)}
                >
                  <Trash2 className="mr-1.5 h-4 w-4" /> Remove block
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* team form */}
      <Dialog open={!!teamForm} onOpenChange={(o) => !o && setTeamForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{teamForm?.id ? "Edit team" : "New installer team"}</DialogTitle>
          </DialogHeader>
          {teamForm && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>Team name *</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  data-testid="team-name-input"
                  value={teamForm.name}
                  onChange={(e) => setTeamForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Team leader full name *</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  data-testid="team-leader-input"
                  value={teamForm.leader_name}
                  onChange={(e) => setTeamForm((f) => ({ ...f, leader_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Team leader mobile *</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  inputMode="numeric"
                  data-testid="team-mobile-input"
                  value={teamForm.leader_mobile}
                  onChange={(e) => setTeamForm((f) => ({ ...f, leader_mobile: e.target.value }))}
                />
              </div>
              {!teamForm.id && (
                <div>
                  <Label>Installer type</Label>
                  <div className="mt-1.5 flex gap-2">
                    {[
                      ["hq", "HQ installer"],
                      ["studio", "Studio installer"],
                    ].map(([k, label]) => (
                      <button
                        key={k}
                        data-testid={`team-type-${k}`}
                        disabled={k === "hq" && !isAdmin}
                        onClick={() => setTeamForm((f) => ({ ...f, installer_type: k }))}
                        className={`min-h-[44px] rounded px-4 text-sm font-medium transition-colors duration-200 disabled:opacity-40 ${
                          (teamForm.installer_type || "studio") === k
                            ? "bg-brand text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1.5 text-xs text-slate-500">
                    HQ installers are contracted by HQ, shared across studios and appear on this
                    grid. Studio installers belong to one studio and are scheduled by the studio.
                  </p>
                </div>
              )}
              {isAdmin && !teamForm.id && (teamForm.installer_type || "studio") === "studio" && (
                <div>
                  <Label>Studio *</Label>
                  <Select
                    value={teamForm.studio_id || ""}
                    onValueChange={(v) => setTeamForm((f) => ({ ...f, studio_id: v }))}
                  >
                    <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="team-studio-select">
                      <SelectValue placeholder="Select studio" />
                    </SelectTrigger>
                    <SelectContent>
                      {studios.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Home base location</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  data-testid="team-home-base-input"
                  value={teamForm.home_base}
                  onChange={(e) => setTeamForm((f) => ({ ...f, home_base: e.target.value }))}
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Skills &amp; certifications</Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="team-skills-picker">
                  {skillOptions.map((sk) => {
                    const on = teamForm.skills.includes(sk.name);
                    return (
                      <button
                        key={sk.id}
                        type="button"
                        data-testid={`team-skill-${sk.name}`}
                        onClick={() =>
                          setTeamForm((f) => ({
                            ...f,
                            skills: on
                              ? f.skills.filter((x) => x !== sk.name)
                              : [...f.skills, sk.name],
                          }))
                        }
                        className={`min-h-[44px] rounded-full px-3 text-xs font-semibold transition-colors duration-200 ${
                          on ? "bg-brand text-white" : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {sk.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2">
                <button
                  type="button"
                  data-testid="team-active-toggle"
                  onClick={() => setTeamForm((f) => ({ ...f, active: !f.active }))}
                  className="flex min-h-[44px] items-center gap-2 text-sm font-medium text-slate-700"
                >
                  {teamForm.active ? (
                    <CircleCheck className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <CircleSlash className="h-4 w-4 text-rose-600" />
                  )}
                  {teamForm.active ? "Active" : "Inactive"}
                </button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              data-testid="save-team-btn"
              className="min-h-[44px]"
              onClick={saveTeam}
              disabled={
                !teamForm?.name ||
                !teamForm?.leader_name ||
                !teamForm?.leader_mobile ||
                (isAdmin &&
                  !teamForm?.id &&
                  (teamForm?.installer_type || "studio") === "studio" &&
                  !teamForm?.studio_id)
              }
            >
              Save team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
